/**
 * Small pill rendering a captured vehicle's vendor-feed sync status.
 * Surfaced on Image Index rows and Vehicle Detail headers so an operator
 * can see at a glance when a session was added ahead of the daily feed.
 *
 * `in_feed`              → render nothing (no chip = expected state)
 * `not_in_feed`          → orange "Not in vendor feed yet"
 * `no_feed_configured`   → gray "No vendor feed"
 *
 * Status values match invi/schemas/core.IN_FEED_STATUS_*.
 */
import React from "react"

import { THEME } from "../../config/theme"
import { tint } from "../../utils/color"
import { InFeedStatus } from "../../api/imageIndex"

interface Props {
  status: InFeedStatus
}

export default function InFeedStatusChip({ status }: Props) {
  if (status === "in_feed") return null
  if (status === "not_in_feed") {
    const accent = THEME.auditTable.tabColors.dealer_duplicates
    return <span style={chipStyle(accent)}>Not in vendor feed yet</span>
  }
  return <span style={mutedChipStyle}>No vendor feed</span>
}

const chipStyle = (accent: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "2px 8px",
  fontSize: 11,
  fontWeight: 500,
  borderRadius: 999,
  background: tint(accent, 0.5),
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${accent}`,
  whiteSpace: "nowrap",
})

const mutedChipStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  fontSize: 11,
  fontWeight: 500,
  borderRadius: 999,
  background: THEME.auditTable.zebraMain,
  color: THEME.colors.neutral.light.textSecondary,
  border: `1px solid ${THEME.colors.silver}`,
  whiteSpace: "nowrap",
}
